(() => {
  'use strict';

  if (window.MinacoCartDrawerController) return;

  const ROOT_SELECTOR = '[data-minaco-cart-drawer]';
  const OPEN_SELECTOR = '[data-minaco-cart-open]';
  const CLOSE_SELECTOR = '[data-minaco-cart-close]';
  const SECTION_HANDLE = 'minaco-cart-drawer';

  const shopRoot = () => (window.Shopify?.routes?.root || '/');
  const getDrawer = () => document.querySelector(ROOT_SELECTOR);
  const getPanel = () => getDrawer()?.querySelector('.minaco-cart-drawer__panel');

  let returnFocus = null;
  let busy = false;

  function setOpen(open, focus = true) {
    const drawer = getDrawer();
    if (!drawer) return;

    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    document.body.classList.toggle('minaco-cart-drawer-open', open);

    if (open) {
      if (focus) requestAnimationFrame(() => getPanel()?.focus({ preventScroll: true }));
      loadRecommendations(drawer);
    } else if (focus && returnFocus instanceof HTMLElement) {
      returnFocus.focus({ preventScroll: true });
      returnFocus = null;
    }
  }

  function openDrawer(trigger) {
    returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
    setOpen(true);
  }

  function closeDrawer() {
    setOpen(false);
  }

  function updateHeaderCount(count) {
    document.querySelectorAll('.minaco-header__cart').forEach((cartLink) => {
      let badge = cartLink.querySelector('.minaco-header__cart-count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'minaco-header__cart-count';
          cartLink.appendChild(badge);
        }
        badge.textContent = String(count);
      } else {
        badge?.remove();
      }
    });
  }

  async function refreshCartCount() {
    try {
      const response = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      updateHeaderCount(cart.item_count || 0);
    } catch (_) {}
  }

  function parseSectionHtml(html) {
    const template = document.createElement('template');
    template.innerHTML = String(html || '').trim();
    return template.content.querySelector(ROOT_SELECTOR);
  }

  async function replaceDrawer(html, keepOpen = true) {
    const current = getDrawer();
    const next = parseSectionHtml(html);
    if (!current || !next) return false;

    current.replaceWith(next);
    if (keepOpen) setOpen(true, false);
    return true;
  }

  async function fetchDrawerSection() {
    const url = new URL(window.location.href);
    url.search = '';
    url.hash = '';
    url.searchParams.set('section_id', SECTION_HANDLE);
    const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(`Cart section request failed: ${response.status}`);
    return response.text();
  }

  async function changeLine(key, quantity) {
    if (busy || !key) return;
    busy = true;

    const drawer = getDrawer();
    drawer?.classList.add('is-busy');
    drawer?.querySelectorAll('[data-cart-quantity-change], [data-cart-remove]').forEach((el) => {
      el.disabled = true;
    });

    try {
      const response = await fetch(`${shopRoot()}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: Math.max(0, quantity),
          sections: [SECTION_HANDLE],
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) throw new Error(`Cart change failed: ${response.status}`);
      const cart = await response.json();
      updateHeaderCount(cart.item_count || 0);

      let replaced = false;
      const rendered = cart.sections?.[SECTION_HANDLE];
      if (rendered) replaced = await replaceDrawer(rendered, true);

      if (!replaced) {
        const fallback = await fetchDrawerSection();
        await replaceDrawer(fallback, true);
      }
    } catch (error) {
      console.error('[MINA&CO cart drawer]', error);
      await refreshCartCount();
    } finally {
      busy = false;
      getDrawer()?.classList.remove('is-busy');
      getDrawer()?.querySelectorAll('[data-cart-quantity-change], [data-cart-remove]').forEach((el) => {
        el.disabled = false;
      });
    }
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function money(cents, currency) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (_) {
      return `${currency || '$'} ${amount.toFixed(2)}`;
    }
  }

  function recommendationCard(product, currency) {
    const image = product.featured_image || product.images?.[0] || '';
    const title = escapeHtml(product.title || '');
    const url = product.url || '#';
    const imageMarkup = image
      ? `<img class="minaco-cart-recommendation__image" src="${escapeHtml(image)}" alt="${title}" loading="lazy">`
      : '';

    return `
      <article class="minaco-cart-recommendation">
        <a class="minaco-cart-recommendation__image-link" href="${escapeHtml(url)}">${imageMarkup}</a>
        <a class="minaco-cart-recommendation__title" href="${escapeHtml(url)}">${title}</a>
        <div class="minaco-cart-recommendation__price">${escapeHtml(money(product.price, currency))}</div>
      </article>`;
  }

  async function loadRecommendations(drawer = getDrawer()) {
    const track = drawer?.querySelector('[data-minaco-cart-recommendation-track]');
    if (!track || track.dataset.loaded === 'true' || track.dataset.loading === 'true') return;

    const productId = drawer.dataset.productId;
    if (!productId) return;

    track.dataset.loading = 'true';
    const limit = 2;
    const intent = drawer.dataset.recommendationIntent || 'related';

    try {
      const url = `${shopRoot()}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}&intent=${encodeURIComponent(intent)}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Recommendations failed: ${response.status}`);
      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products.slice(0, 2) : [];

      if (!products.length) {
        drawer.querySelector('[data-minaco-cart-recommendations]')?.setAttribute('hidden', '');
        return;
      }

      const currency = drawer.dataset.currency || window.Shopify?.currency?.active || 'USD';
      track.innerHTML = products.map((product) => recommendationCard(product, currency)).join('');
      drawer.querySelector('[data-minaco-cart-duty-notice]')?.removeAttribute('hidden');
      track.dataset.loaded = 'true';
    } catch (error) {
      console.warn('[MINA&CO recommendations]', error);
    } finally {
      delete track.dataset.loading;
    }
  }

  function onClick(event) {
    const opener = event.target.closest(OPEN_SELECTOR);
    if (opener) {
      event.preventDefault();
      openDrawer(opener);
      return;
    }

    if (event.target.closest(CLOSE_SELECTOR)) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const drawer = event.target.closest(ROOT_SELECTOR);
    if (!drawer) return;

    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      const line = remove.closest('[data-cart-line]');
      changeLine(line?.dataset.lineKey, 0);
      return;
    }

    const quantityButton = event.target.closest('[data-cart-quantity-change]');
    if (quantityButton) {
      const line = quantityButton.closest('[data-cart-line]');
      const current = Number(line?.querySelector('[data-cart-quantity]')?.textContent || 0);
      const delta = Number(quantityButton.dataset.cartQuantityChange || 0);
      changeLine(line?.dataset.lineKey, current + delta);
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape' && getDrawer()?.classList.contains('is-open')) {
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;
    const drawer = getDrawer();
    if (!drawer?.classList.contains('is-open')) return;

    const focusables = [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((el) => !el.hasAttribute('hidden') && el.offsetParent !== null);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);

  document.addEventListener('shopify:section:load', (event) => {
    const drawer = event.target?.querySelector?.(ROOT_SELECTOR) || (event.target?.matches?.(ROOT_SELECTOR) ? event.target : null);
    if (drawer) loadRecommendations(drawer);
  });

  window.MinacoCartDrawerController = {
    open: openDrawer,
    close: closeDrawer,
    refresh: async () => {
      const html = await fetchDrawerSection();
      await replaceDrawer(html, getDrawer()?.classList.contains('is-open'));
      await refreshCartCount();
    }
  };
})();
